import { useState, useCallback } from "react";
import { useConfirmDialog } from "@tabipla/hooks/confirm-dialog";
import type { SpotResponseDto } from "@tabipla/shared";

// ==================== Types ====================

export type DialogName =
  | "share"
  | "coverEditor"
  | "aiGenerate"
  | "editTrip"
  | "invite"
  | "groupSelect"
  | "quickExpense";

export interface DialogState {
  share: boolean;
  coverEditor: boolean;
  aiGenerate: boolean;
  editTrip: boolean;
  invite: boolean;
  groupSelect: boolean;
  quickExpense: boolean;
}

export interface SpotDialogState {
  addSpotItineraryId: string | null;
  addSpotStartTime: string | null;
  editingSpot: SpotResponseDto | null;
}

export interface UseTripDialogsReturn {
  // Dialog states
  dialogs: DialogState;
  spotDialogs: SpotDialogState;

  // Open actions
  open: {
    share: () => void;
    coverEditor: () => void;
    aiGenerate: () => void;
    editTrip: () => void;
    invite: () => void;
    groupSelect: () => void;
    quickExpense: () => void;
  };

  // Close action
  close: (name: DialogName) => void;

  // Toggle action
  toggle: (name: DialogName) => void;

  // Spot-specific actions
  setAddSpotItineraryId: (id: string | null, startTime?: string | null) => void;
  setEditingSpot: (spot: SpotResponseDto | null) => void;

  // Confirm dialog
  confirm: ReturnType<typeof useConfirmDialog>["confirm"];
  confirmDialogProps: ReturnType<typeof useConfirmDialog>["dialogProps"];
}

// ==================== Initial State ====================

const initialDialogState: DialogState = {
  share: false,
  coverEditor: false,
  aiGenerate: false,
  editTrip: false,
  invite: false,
  groupSelect: false,
  quickExpense: false,
};

const initialSpotDialogState: SpotDialogState = {
  addSpotItineraryId: null,
  addSpotStartTime: null,
  editingSpot: null,
};

// ==================== Hook ====================

export function useTripDialogs(): UseTripDialogsReturn {
  const { confirm, dialogProps: confirmDialogProps } = useConfirmDialog();

  // Dialog states
  const [dialogs, setDialogs] = useState<DialogState>(initialDialogState);
  const [spotDialogs, setSpotDialogs] = useState<SpotDialogState>(initialSpotDialogState);

  // Open actions
  const openShare = useCallback(() => setDialogs((s) => ({ ...s, share: true })), []);
  const openCoverEditor = useCallback(() => setDialogs((s) => ({ ...s, coverEditor: true })), []);
  const openAiGenerate = useCallback(() => setDialogs((s) => ({ ...s, aiGenerate: true })), []);
  const openEditTrip = useCallback(() => setDialogs((s) => ({ ...s, editTrip: true })), []);
  const openInvite = useCallback(() => setDialogs((s) => ({ ...s, invite: true })), []);
  const openGroupSelect = useCallback(() => setDialogs((s) => ({ ...s, groupSelect: true })), []);
  const openQuickExpense = useCallback(() => setDialogs((s) => ({ ...s, quickExpense: true })), []);

  // Close action
  const close = useCallback((name: DialogName) => {
    setDialogs((s) => ({ ...s, [name]: false }));
  }, []);

  // Toggle action
  const toggle = useCallback((name: DialogName) => {
    setDialogs((s) => ({ ...s, [name]: !s[name] }));
  }, []);

  // Spot-specific actions
  const setAddSpotItineraryId = useCallback((id: string | null, startTime?: string | null) => {
    setSpotDialogs((s) => ({
      ...s,
      addSpotItineraryId: id,
      addSpotStartTime: startTime ?? null,
    }));
  }, []);

  const setEditingSpot = useCallback((spot: SpotResponseDto | null) => {
    setSpotDialogs((s) => ({ ...s, editingSpot: spot }));
  }, []);

  return {
    // Dialog states
    dialogs,
    spotDialogs,

    // Open actions
    open: {
      share: openShare,
      coverEditor: openCoverEditor,
      aiGenerate: openAiGenerate,
      editTrip: openEditTrip,
      invite: openInvite,
      groupSelect: openGroupSelect,
      quickExpense: openQuickExpense,
    },

    // Close action
    close,

    // Toggle action
    toggle,

    // Spot-specific actions
    setAddSpotItineraryId,
    setEditingSpot,

    // Confirm dialog
    confirm,
    confirmDialogProps,
  };
}
